/**
 * ReportsOverview — faithful port of the canonical reports screen.
 * Tabs: Overview · By event · By guests · By campaign · Revenue
 * KPI scorecard, executive summary, charts (SVG bars + funnel), leaderboards.
 */
import { useState, useMemo, useEffect, type ReactNode } from "react";
import { alpha, Btn, Card, Dropdown, Icon, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { getReportsOverview, reportsMoney, listLocations } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS, REPORTS_MONEY_OPTS, REPORTS_OVERVIEW_OPTS } from "../../shell/request-opts";
import ReportsOverviewAdmin from "./ReportsOverviewAdmin";
import { can } from "../../shell/access";
import { moduleKnownAbsent } from "../../lib/tenant-modules";
import { useT, t as i18nT } from "../../lib/i18n";

/* ── Helpers ─────────────────────────────────────────────────────── */
export function fmtNum(n: number) { return n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n); }
export function fmtMoney(n: number) { return `$${n >= 1000 ? `${(n/1000).toFixed(0)}k` : n}`; }
export function fmtMoneyFull(n: number) { return `$${n.toLocaleString()}`; }
export function fmtPct(n: number, d=0) { return `${(n*100).toFixed(d)}%`; }

/* ── Range + Location selectors ──────────────────────────────────── */
export const RANGES = [ // ui-config
  { id:"7d",  label:"Last 7 days",  subtitle:"recent" },
  { id:"30d", label:"Last 30 days", subtitle:"month" },
  { id:"90d", label:"Last 90 days", subtitle:"quarter" },
  { id:"ytd", label:"Year to date", subtitle:"YTD" },
  { id:"12m", label:"Last 12 months", subtitle:"12M" },
];

export function RangeSelect({ value, onChange }: { value:string; onChange:(v:string)=>void }) {
  const t = useT();
  return (
    <Dropdown<string> portal aria-label={t("reports.analytics.range", "Date range")} value={value} onChange={onChange} style={{ width:170 }}
      options={RANGES.map((r)=>({ value:r.id, label:r.label }))} />
  );
}
export function LocationSelect({ value, onChange, locs }: { value:string; onChange:(v:string)=>void; locs:{id:string;name:string}[] }) {
  const t = useT();
  return (
    <Dropdown<string> portal aria-label={t("reports.analytics.location", "Location")} value={value} onChange={onChange} style={{ width:200 }}
      options={[{id:"all",name:t("reports.overview.allLocations", "All locations")},...locs].map((l)=>({ value:l.id, label:l.name }))} />
  );
}

/* ── Tabs ────────────────────────────────────────────────────────── */
/** Three groups, not eight peers (operator, 2026-08-31). The tabs answer three
 *  different questions owned by three different people: whether students are
 *  being reached, whether sessions run well, and whether the money works. A
 *  flat strip asserted they were interchangeable, which is how Reports went
 *  unread before Campus Pulse existed.
 *
 *  "By event" was merged into Events Overview — it ranked the same occurrences
 *  the console already holds. Its route stays live for deep links. */
/** The tabs sit in three ORDERED clusters — student-facing, event operations,
 *  money — separated by a rule rather than by a label (operator, 2026-08-31:
 *  the group words were noise). Order and the divider carry the grouping; the
 *  `group` key is what PrimaryNav draws the divider from, so it stays as an
 *  internal id and is never rendered. */
const G_SUCCESS = "g1";
const G_EVENTS = "g2";
const G_MONEY = "g3";

export function reportTabs(nav: ReturnType<typeof useNav>, active: string) {
  const tabs = [
    { id:"pulse",     group:G_SUCCESS, label:i18nT("reports.overview.campusPulse", "Campus Pulse"), onClick:()=>nav.go("reports") },
    /* Renamed 2026-08-28: this tab has always been the events fill-rate/occupancy
     * console (ReportsOverviewAdmin), not a workspace-wide overview. Moved to its
     * own route 2026-08-31 when Campus Pulse became the default report. */
    { id:"overview",  group:G_EVENTS,  label:i18nT("reports.overview.eventsOverview", "Events Overview"), onClick:()=>nav.go("reports.events") },
    /* Event reports library (2026-08-14): the route existed ("reports.library",
     * the canned-reports hub) but nothing in the nav linked it — reachable only
     * by direct URL. */
    { id:"library",   group:G_EVENTS,  label:i18nT("reports.overview.eventReports", "Event reports"), onClick:()=>nav.go("reports.library") },
    /* By attendee, By campaign and Revenue rolled into Events Overview (2026-10-05, Nick): their
       routes now open the matching Overview section. */
  ];
  // Appointments reports family (APT-10, 2026-07-26) — shown once the
  // appointments process is readable for the signed-in role. It reads the same
  // interaction record as Campus Pulse from the service side, so it belongs in
  // the student-success group, second.
  /* A tenant known not to compose booking has no Campus Pulse; `reports` opens on the events
     overview there (CC, 3 Oct). */
  if (moduleKnownAbsent("booking")) tabs.splice(tabs.findIndex((x) => x.id === "pulse"), 1);
  if (can("appointments.read")) {
    tabs.splice(1, 0, { id:"appointments", group:G_SUCCESS, label:i18nT("reports.overview.appointments", "Appointments"), onClick:()=>nav.go("reports.appointments") });
  }
  /* By event keeps its tab ONLY while the reader is on it (a deep link must not
     land on a screen with no tab of its own). */
  if (active === "events") {
    tabs.splice(tabs.findIndex((x) => x.id === "library"), 0, { id:"events", group:G_EVENTS, label:i18nT("reports.overview.byEvent", "By event"), onClick:()=>nav.go("reports.event") });
  }
  return tabs;
}

/* ── KPI tile ────────────────────────────────────────────────────── */
export interface DeltaInfo { text:string; up:boolean; pct:number; }
export function delta(cur:number, prev:number, lowerBetter=false): DeltaInfo {
  const pct = prev>0 ? ((cur-prev)/prev)*100 : 0;
  const up = pct>0;
  const good = lowerBetter ? !up : up;
  return { text:`${up?"↑":"↓"} ${Math.abs(pct).toFixed(0)}% vs prev`, up, pct };
}
export function ScoreTile({ label,value,sub,delta:d,accent,icon,goal }:{label:string;value:string;sub?:string;delta?:DeltaInfo;accent:string;icon?:ReactNode;goal?:{actual:number;target:number}}) {
  const t = useT();
  return (
    <Card style={{ padding:16 }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:8 }}>
        <div style={{ fontSize:12, fontWeight:600, color:T.soft, textTransform:"uppercase" as const, letterSpacing:0.04 }}>{label}</div>
        {icon ? <span style={{ color:accent }}>{icon}</span> : null}
      </div>
      <div style={{ fontSize:26, fontWeight:800, color:T.n1, fontVariantNumeric:"tabular-nums" }}>{value}</div>
      {d ? <div style={{ fontSize:12, color:d.up?T.green1:T.red1, marginTop:4, fontWeight:500 }}>{d.text}</div> : null}
      {sub ? <div style={{ fontSize:12, color:T.soft, marginTop:4 }}>{sub}</div> : null}
      {goal && goal.target>0 ? (
        <div style={{ marginTop:8 }}>
          <div style={{ height:4, background:T.n5, borderRadius:999, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${Math.min(100,(goal.actual/goal.target)*100)}%`, background:accent, transition:"width 400ms" }}/>
          </div>
          <div style={{ fontSize:11, color:T.soft, marginTop:3 }}>{Math.round((goal.actual/goal.target)*100)}{t("reports.overview.ofGoal", "% of goal")}</div>
        </div>
      ) : null}
    </Card>
  );
}

/* ── Simple bar chart (SVG) ──────────────────────────────────────── */
export function BarChart({ series, labels, height=160 }:{series:{label:string;reg:number;att:number;rev:number}[];labels:string[];height?:number}) {
  const maxVal = Math.max(...series.map((s)=>Math.max(s.reg,s.att)));
  return (
    <div style={{ position:"relative", height, display:"flex", alignItems:"flex-end", gap:4 }}>
      {series.map((s,i)=>(
        <div key={i} style={{ flex:1, display:"flex", gap:1, alignItems:"flex-end", flexDirection:"column" }}>
          <div style={{ display:"flex", gap:1, alignItems:"flex-end", width:"100%" }}>
            <div title={i18nT("reports.overview.registeredSReg.icu", "Registered: {value0}", { value0: s.reg })} style={{ flex:1, background:T.p1, borderRadius:"3px 3px 0 0", height:`${Math.max(4,(s.reg/maxVal)*height)}px`, opacity:0.9 }}/>
            <div title={i18nT("reports.overview.attendedSAtt.icu", "Attended: {value0}", { value0: s.att })}   style={{ flex:1, background:T.green1, borderRadius:"3px 3px 0 0", height:`${Math.max(4,(s.att/maxVal)*height)}px`, opacity:0.85 }}/>
          </div>
          <div style={{ fontSize:10, color:T.soft, textAlign:"center" as const, width:"100%", marginTop:3 }}>{labels[i]}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Funnel ──────────────────────────────────────────────────────── */
function FunnelChart({ steps }:{steps:{label:string;n:number;pct:number;color:string}[]}) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
      {steps.map((s,i)=>(
        <div key={i} style={{ display:"flex", flexDirection:"column", gap:3 }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
            <span style={{ color:T.n1, fontWeight:600 }}>{s.label}</span>
            <span style={{ color:T.soft, fontVariantNumeric:"tabular-nums" }}>{fmtNum(s.n)} · {fmtPct(s.pct/100,0)}</span>
          </div>
          <div style={{ height:6, background:T.n5, borderRadius:999, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${s.pct}%`, background:s.color, borderRadius:999 }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══ Main screen ════════════════════════════════════════════════════ */
/** Map the dedicated getReportsOverview response onto the screen shape.
 *  Money fields arrive in minor units → converted to major once, here.
 *  `prev` is the response's REAL `prior` period — when a prior figure is
 *  absent the field is null and the UI omits the delta (never a scaled ratio). */
export function toReportData(res: any, _range: string): ReturnType<typeof buildEmpty> {
  const m$ = (minor: unknown) => (typeof minor === "number" ? minor / 100 : 0);
  const k = res?.kpis ?? {};
  const prior = res?.prior ?? null;
  const FUNNEL_COLORS = [T.p1, "#0E7490", "#1D7A3A", "#1F8A5B"]; // ui-config
  return {
    kpis: { registered: k.registered ?? 0, attended: k.attended ?? 0, gross: m$(k.grossRevenueMinor), net: m$(k.netRevenueMinor), fees: m$(k.feesMinor), refunds: m$(k.refundsMinor), attRate: k.attendanceRate ?? 0, noShowRate: k.noShowRate ?? 0 },
    prev: {
      registered: prior?.registered ?? null,
      attended: prior?.attended ?? null,
      gross: prior?.grossRevenueMinor != null ? m$(prior.grossRevenueMinor) : null,
      net: prior?.netRevenueMinor != null ? m$(prior.netRevenueMinor) : null,
      attRate: prior?.attendanceRate ?? null,
      noShowRate: prior?.noShowRate ?? null,
    },
    funnel: (res?.funnel ?? []).map((f: any, i: number, arr: any[]) => ({ label: f.label ?? "Stage " + (i + 1), n: f.value ?? 0, pct: arr[0]?.value ? Math.round(((f.value ?? 0) / arr[0].value) * 100) : 0, color: FUNNEL_COLORS[i] ?? T.p1 })),
    bySource: (res?.bySource ?? res?.sourceMix ?? []).map((s: any) => ({ id: s.id ?? s.source, label: s.label ?? s.source, color: s.color ?? T.p1, reg: s.reg ?? s.registrations ?? 0, pct: s.pct ?? Math.round((s.share ?? 0) * 100), revenue: m$(s.revenueMinor) })),
    byEvent: (res?.topEvents ?? res?.byEvent ?? []).slice(0, 5).map((e: any) => ({ id: e.id, title: e.title ?? "Event", hue: e.hue ?? T.p1, registered: e.registered ?? 0, revenue: e.revenueMinor != null ? m$(e.revenueMinor) : e.revenue ?? 0, fill: e.fill ?? (e.capacity ? (e.registered ?? 0) / e.capacity : 0), location: e.locationName ?? e.location ?? "" })),
    byLocation: (res?.byLocation ?? res?.locations ?? []).map((l: any) => ({ id: l.id, name: l.name ?? "Location", reg: l.reg ?? l.registrations ?? 0, revenue: l.revenueMinor != null ? m$(l.revenueMinor) : l.revenue ?? 0 })),
    series: (res?.series ?? []).map((w: any, i: number) => ({ label: w.label ?? "W" + (i + 1), reg: w.registered ?? 0, att: w.attended ?? 0, rev: m$(w.revenueMinor) })),
  };
}
export function buildEmpty(): {
  kpis: { registered: number; attended: number; gross: number; net: number; fees: number; refunds: number; attRate: number; noShowRate: number };
  prev: { registered: number | null; attended: number | null; gross: number | null; net: number | null; attRate: number | null; noShowRate: number | null };
  funnel: { label: string; n: number; pct: number; color: string }[];
  bySource: { id: string; label: string; color: string; reg: number; pct: number; revenue: number }[];
  byEvent: { id: string; title: string; hue: string; registered: number; revenue: number; fill: number; location: string }[];
  byLocation: { id: string; name: string; reg: number; revenue: number }[];
  series: { label: string; reg: number; att: number; rev: number }[];
} {
  return { kpis:{ registered:0,attended:0,gross:0,net:0,fees:0,refunds:0,attRate:0,noShowRate:0 }, prev:{ registered:null,attended:null,gross:null,net:null,attRate:null,noShowRate:null }, funnel:[], bySource:[], byEvent:[], byLocation:[], series:[] };
}

/** Every reports.read role now sees the fill-rate/occupancy console (PM
 * decision 2026-07-10, ingested 2026-07-13: "update all roles that have access
 * to Report Overview to match the new report on the Administrator role").
 * The console is capability-aware — the Export menu + roster/registration CSVs
 * gate on `reports.export` (can()), so a role without it (e.g. Event Creator)
 * sees the same report with export actions disabled. ReportsOverviewStandard
 * (the previous executive scorecard) stays defined + exported for
 * reference/rollback; it is no longer routed. */
export default function ReportsOverview() {
  return <ReportsOverviewAdmin />;
}

export function ReportsOverviewStandard() {
  const t = useT();
  const nav = useNav();
  const [range, setRange] = useState("30d");
  const [location, setLocation] = useState("all");
  const [rawData, setRawData] = useState<any>(null);
  const [locs, setLocs] = useState<{id:string;name:string}[]>([]);
  useEffect(() => { listLocations({}, LOCATIONS_OPTS).then((r:any)=>{ const arr = r.locations??r.data??[]; setLocs(arr.map((l:any)=>({ id:l.id, name:l.name??l.locationName??"Location" }))); }).catch(()=>{}); }, []);
  const b = useMemo(() => rawData ? toReportData(rawData, range) : buildEmpty(), [rawData, range]);
  useEffect(() => {
    getReportsOverview({ range, ...(location === "all" ? {} : { locations: location }) } as any, REPORTS_OVERVIEW_OPTS)
      .then((res: any) => setRawData(res)).catch(() => setRawData({}));
  }, [range, location]);
  // Real goal targets for the KPI goal bars — getReportGoals pacing[] (scaled by the backend).
  const [pacing, setPacing] = useState<any[]>([]);
  useEffect(() => {
    let live = true;
    reportsMoney.getReportGoals({ range } as any, REPORTS_MONEY_OPTS)
      .then((res: any) => { if (live) setPacing(res?.pacing ?? []); })
      .catch(() => { if (live) setPacing([]); });
    return () => { live = false; };
  }, [range]);
  const regTarget = pacing.find((x: any) => x.metric === "registrations")?.target ?? null;
  const revPace = pacing.find((x: any) => x.metric === "revenue") ?? null;
  const rangeMeta = RANGES.find((r)=>r.id===range) ?? RANGES[1];
  const locName = location==="all" ? "all locations" : (locs.find((l)=>l.id===location)?.name ?? "all locations");
  const k=b.kpis, p=b.prev;
  const dReg = p.registered!=null ? delta(k.registered,p.registered) : undefined;
  const dRev = p.gross!=null ? delta(k.gross,p.gross) : undefined;
  const dAtt = p.attRate!=null ? delta(k.attRate,p.attRate) : undefined;
  const dNoShow = p.noShowRate!=null ? delta(k.noShowRate,p.noShowRate,true) : undefined;
  const dNet = p.net!=null ? delta(k.net,p.net) : undefined;

  const insight = dReg && p.registered!=null && p.attRate!=null
    ? `Registrations are ${dReg.up?"up":"down"} ${Math.abs(dReg.pct).toFixed(0)}% vs the prior period (${fmtNum(k.registered)} vs ${fmtNum(p.registered)}). Attendance rate ${fmtPct(k.attRate)} — ${k.attRate>=p.attRate?"above":"below"} the prior period's ${fmtPct(p.attRate)}.${dRev?` Gross revenue ${dRev.text}.`:""}`
    : `${fmtNum(k.registered)} registered and ${fmtNum(k.attended)} attended this period. Attendance rate ${fmtPct(k.attRate)} · gross revenue ${fmtMoney(k.gross)}. No prior-period comparison for this range.`;

  return (
    <Screen rail="reports" tabs={reportTabs(nav,"overview")} activeTab="overview">
      <PageHeader
        title={t("reports.overview.reports", "Reports")}
        subtitle={i18nT("reports.overview.rangemetaLabelTolowercaseLocname.icu", "{value0} · {value1}", { value0: rangeMeta.label.toLowerCase(), value1: locName })}
        breadcrumb={<span style={{ cursor:"pointer" }} onClick={() => nav.go("dashboard")}>{t("reports.overview.dashboard", "← Dashboard")}</span>}
        actions={<>
          <LocationSelect value={location} onChange={setLocation} locs={locs}/>
          <RangeSelect value={range} onChange={(v)=>{ setRange(v); toast({ tone:"info", title:i18nT("reports.overview.rangeRangesFindRRIdVLabel.icu", "Range · {value0}", { value0: RANGES.find((r)=>r.id===v)?.label }), subtitle:t("reports.overview.allChartsAndKpisUpdated", "All charts and KPIs updated.") }); }}/>
          <Btn variant="secondary" icon={<Icon.download size={14}/>} onClick={() => toast({ tone:"info", title:t("reports.overview.exportStarted", "Export started"), subtitle:t("reports.overview.csvDownloadWillBeginShortly", "CSV download will begin shortly.") })}>{t("common.actions.export", "Export")}</Btn>
        </>}
      />

      <div style={{ flex:1, overflow:"auto", padding:"16px 24px 24px", display:"flex", flexDirection:"column", gap:16 }}>
        {/* Executive summary */}
        <Card style={{ padding:16, background:T.p4, borderColor:T.p2, display:"flex", gap:12, alignItems:"flex-start" }}>
          <span style={{ width:28, height:28, borderRadius:6, background:T.p1, color:T.white, display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon.chart size={15}/></span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.p1, textTransform:"uppercase" as const, letterSpacing:0.04 }}>{t("reports.overview.executiveSummary", "Executive summary ·")}{rangeMeta.label.toLowerCase()}</div>
            <div style={{ fontSize:14, color:T.n1, marginTop:4, lineHeight:1.5, textWrap:"pretty" as any }}>{insight}</div>
          </div>
          {revPace == null ? null : revPace.aheadOfGoal ? <Pill tone="success" dot>{t("reports.overview.onTrack", "On track")}</Pill> : <Pill tone="warn" dot>{t("reports.overview.behindGoal", "Behind goal")}</Pill>}
        </Card>

        {/* KPI scorecard */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:12 }}>
          <ScoreTile label={t("reports.overview.registered", "Registered")}     value={fmtNum(k.registered)}   delta={dReg}   accent={T.p1}      icon={<Icon.users  size={14}/>} goal={regTarget!=null ? { actual:k.registered, target:regTarget } : undefined}/>
          <ScoreTile label={t("reports.overview.grossRevenue", "Gross revenue")}  value={fmtMoney(k.gross)}       delta={dRev}   accent={T.orange1} icon={<Icon.ticket size={14}/>} goal={revPace!=null ? { actual:k.gross, target:revPace.target/100 } : undefined}/>
          <ScoreTile label={t("reports.overview.attendance", "Attendance")}     value={fmtPct(k.attRate)}       delta={dAtt}   accent={T.green1}  icon={<Icon.check  size={14}/>}/>
          <ScoreTile label={t("reports.overview.noShowRate", "No-show rate")}   value={fmtPct(k.noShowRate)}    delta={dNoShow} accent={T.red1}   icon={<Icon.warn   size={14}/>} sub={t("reports.overview.sub", "Ceiling 5%")}/>
          <ScoreTile label={t("reports.overview.netRevenue", "Net revenue")}    value={fmtMoney(k.net)}         delta={dNet}   accent={T.p1}  icon={<Icon.chart  size={14}/>} sub={t("reports.overview.sub3", "After {arg1} fees", { arg1: fmtMoney(k.fees) })}/>
        </div>

        {/* Chart row */}
        <div style={{ display:"grid", gridTemplateColumns:"1.5fr 1fr", gap:16 }}>
          <Card style={{ padding:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
              <div>
                <h3 style={{ ...tx.h3, fontWeight:600 }}>{t("reports.overview.registrationsAttendance", "Registrations & attendance")}</h3>
                <p style={{ ...tx.help, marginTop:2 }}>{rangeMeta.label.toLowerCase()}{location!=="all"?t("reports.overview.text", " · {locName}", { locName }):""}</p>
              </div>
              <div style={{ display:"flex", gap:12, fontSize:12, color:T.soft }}>
                <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:T.p1 }}/> {t("reports.overview.registered", "Registered")}</span>
                <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:T.green1 }}/> {t("reports.overview.attended", "Attended")}</span>
              </div>
            </div>
            <BarChart series={b.series} labels={b.series.map((s)=>s.label)} height={160}/>
          </Card>
          <Card>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight:600 }}>{t("reports.overview.conversionFunnel", "Conversion funnel")}</h3>
            </div>
            <div style={{ padding:18 }}><FunnelChart steps={b.funnel}/></div>
          </Card>
        </div>

        {/* Leaderboards */}
        <div style={{ display:"grid", gridTemplateColumns:"1.3fr 1fr 1fr", gap:16 }}>
          <Card>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ ...tx.h3, fontWeight:600 }}>{t("reports.overview.topEvents", "Top events")}</h3>
              <Btn variant="link" size="sm" onClick={() => nav.go("reports.event")}>{t("reports.overview.benchmark", "Benchmark →")}</Btn>
            </div>
            <ul style={{ listStyle:"none", margin:0, padding:0 }}>
              {b.byEvent.slice(0,5).map((ev,i)=>(
                <li key={ev.id} onClick={() => nav.go("reports.event")} style={{ padding:"12px 16px", display:"grid", gridTemplateColumns:"auto 1fr auto auto", gap:12, alignItems:"center", borderTop:i?`1px solid ${T.border}`:"none", cursor:"pointer" }}>
                  <span style={{ width:22, color:T.soft, fontWeight:700, fontSize:13, textAlign:"center" as const }}>{i+1}</span>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, color:T.n1, fontWeight:600, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{ev.title}</div>
                    <div style={{ fontSize:11, color:T.soft }}>{ev.location} · {fmtMoney(ev.revenue)}</div>
                  </div>
                  <div style={{ width:60 }}>
                    <div style={{ height:4, background:T.n5, borderRadius:999, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${Math.min(100,ev.fill*100)}%`, background:ev.hue }}/>
                    </div>
                  </div>
                  <span style={{ fontSize:13, color:T.n1, fontWeight:600, fontVariantNumeric:"tabular-nums", width:42, textAlign:"right" as const }}>{fmtNum(ev.registered)}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight:600 }}>{t("reports.overview.sourceRegsRevenue", "Source · regs & revenue")}</h3>
            </div>
            <ul style={{ listStyle:"none", margin:0, padding:0 }}>
              {b.bySource.map((s,i)=>(
                <li key={s.id} style={{ padding:"11px 16px", borderTop:i?`1px solid ${T.border}`:"none", display:"flex", flexDirection:"column", gap:5 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontSize:13, color:T.n1, fontWeight:600, display:"inline-flex", alignItems:"center", gap:8 }}>
                      <span style={{ width:10, height:10, borderRadius:3, background:s.color }}/>{s.label}
                    </span>
                    <span style={{ fontSize:12, color:T.soft }}>{s.pct}% · {fmtMoney(s.revenue)}</span>
                  </div>
                  <div style={{ height:4, background:T.n5, borderRadius:999, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${s.pct}%`, background:s.color }}/>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ ...tx.h3, fontWeight:600 }}>{t("reports.overview.locations", "Locations")}</h3>
              {location!=="all" ? <Btn variant="link" size="sm" onClick={() => setLocation("all")}>{t("common.actions.clear", "Clear")}</Btn> : null}
            </div>
            <ul style={{ listStyle:"none", margin:0, padding:0 }}>
              {b.byLocation.map((l,i)=>{
                const active = location===l.id;
                return (
                  <li key={l.id} onClick={() => setLocation(active?"all":l.id)} style={{ padding:"12px 16px", display:"flex", gap:12, alignItems:"center", borderTop:i?`1px solid ${T.border}`:"none", cursor:"pointer", background:active?T.p4:"transparent" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, color:T.n1, fontWeight:600 }}>{l.name}</div>
                      <div style={{ fontSize:11, color:T.soft, marginTop:2 }}>{fmtNum(l.reg)} {t("reports.overview.registeredb630ba34", "registered ·")}{fmtMoney(l.revenue)}</div>
                    </div>
                    <div style={{ height:4, width:60, background:T.n5, borderRadius:999, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${(l.reg/b.kpis.registered)*100}%`, background:T.p1 }}/>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
      </div>
    </Screen>
  );
}
